// Asset url helpers for optional runtime feature and worker bundles.
// They resolve files relative to the active core `lentystyle(.min).js` script.
import type { AssetInfo } from './types'
import { isAllowedRuntimeScriptUrl } from '@lentystyle/guard/runtime'
import {
  captureRuntimeCoreScriptElement,
  resolveRuntimeCoreScriptInfo,
} from '@lentystyle/guard/runtime'

// Capture core script src during initial runtime evaluation.
export function captureRuntimeCoreScriptSource(): void {
  captureRuntimeCoreScriptElement()
}

// Resolve one dist asset url from current core-script location.
export function resolveRuntimeAssetUrl(filename: string): string | null {
  // Abort url resolution outside browser environments.
  if (!isBrowser()) {
    // Return null when not in browser context.
    return null
  } else {
    // Continue core-script asset resolution.
  }

  const assetInfo = resolveCoreAssetInfo()
  // Abort when runtime cannot identify core script base path.
  if (!assetInfo) {
    // Return null when core script base path is not available.
    return null
  } else {
    // Continue runtime asset url composition.
  }

  const resolvedAssetUrl = new URL(filename, assetInfo.baseHref).toString()
  // Block asset URL when runtime script policy rejects this origin/path.
  if (!isAllowedRuntimeScriptUrl(resolvedAssetUrl, assetInfo.baseHref)) {
    return null
  } else {
    // Return full runtime asset URL for policy-allowed assets.
    return resolvedAssetUrl
  }
}

// Resolve current runtime minification mode from core script path.
export function isRuntimeMinifiedBuild(): boolean {
  const assetInfo = resolveCoreAssetInfo()
  // Return false when current core asset is unknown.
  return assetInfo?.minified ?? false
}

// Resolve core script base path and minified mode.
function resolveCoreAssetInfo(): AssetInfo | null {
  const coreScript = resolveRuntimeCoreScriptInfo()
  // Return null when the runtime core script cannot be identified.
  if (!coreScript) {
    // Return null for unknown runtime core script metadata.
    return null
  } else {
    // Continue asset-info projection from shared core-script metadata.
  }

  // Return the shared runtime core asset metadata shape.
  return {
    baseHref: coreScript.baseHref,
    minified: coreScript.minified,
  }
}

// Detect browser runtime before touching DOM APIs.
function isBrowser(): boolean {
  // Return true in browser-like environments.
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}
